import React, { useState } from 'react';
import { Clear, Search } from '@mui/icons-material';
import {
  Box,
  DialogContent,
  DialogProps,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material';

import { FullHeightSlideDialog } from '~components/atoms/custom/Dialog';
import { LocalizationTypography } from '~components/organisms';
import { CountryItem } from '~configs';
import useSearchCountry from '~hooks/useSearchCountry';
import { useLanguageContext } from '~providers';

type Props = DialogProps & {
  onSelected: (country: CountryItem) => void;
  currentValue: CountryItem | undefined;
  onClose: VoidFunction;
};

export const CountryCodeSelectionDialog: React.FC<Props> = ({
  onSelected,
  currentValue,
  onClose,
  ...dialogProps
}) => {
  const { lt } = useLanguageContext();
  const [search, setSearch] = useState('');
  const countries = useSearchCountry(search);

  return (
    <FullHeightSlideDialog $height={64} onClose={onClose} {...dialogProps}>
      <DialogTitle textAlign="center">
        <LocalizationTypography
          textAlign="center"
          textId="app.rentalPage.stepOne.countryRegion"
          defaultText="Country/Region"
          variant="Poppins/Heading-5"
        />
        <Box position="absolute" top={8} right={8}>
          <IconButton
            onClick={onClose}
            edge="end"
            size="small"
            sx={{ marginRight: 0.5 }}
          >
            <Clear fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <TextField
            placeholder={lt('text.search', 'Search')}
            sx={(theme) => ({
              '& input::placeholder': {
                ...theme.typography['Inter/Body-sm'],
                color: theme.palette.text.secondary,
              },
              width: 1,
              pt: 0.5,
              backgroundColor: 'white',
              zIndex: 100,
              pb: 1.5,
              height: 52,
              position: 'sticky',
              top: 0,
            })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <Stack marginLeft={1.5} marginRight={1} color="text.secondary">
                  <Search fontSize="small" />
                </Stack>
              ),
              endAdornment: search ? (
                <IconButton
                  onClick={() => setSearch('')}
                  edge="end"
                  size="small"
                  sx={{ marginRight: 0.5 }}
                >
                  <Clear fill="text.secondary" fontSize="inherit" />
                </IconButton>
              ) : null,
              sx: {
                borderRadius: 100,
                padding: '5px 0 !important',
              },
            }}
            inputProps={{
              sx: {
                height: 40,
                padding: '0 !important',
                boxSizing: 'border-box',
              },
              autoComplete: 'off',
            }}
          />
          <RadioGroup
            value={`${currentValue?.dialCode}${currentValue?.name}`}
            sx={{ width: 1, px: 0.5 }}
          >
            {countries.length > 0 &&
              countries.map((country, index) => (
                <FormControlLabel
                  key={index + country.name}
                  sx={{
                    '&.MuiFormControlLabel-root': {
                      py: 0.5,
                      justifyContent: 'space-between',
                      marginLeft: 0,
                    },
                  }}
                  componentsProps={{
                    typography: {
                      variant: 'Inter/Body-md',
                    },
                  }}
                  labelPlacement="start"
                  value={`${country.dialCode}${country.name}`} //Avoid same dialCode or same name case
                  control={<Radio size="medium" />}
                  label={`${country.name} (+${country.dialCode})`}
                  onChange={() => {
                    onSelected(countries[index]);
                    onClose();
                  }}
                />
              ))}

            {countries.length === 0 && (
              <LocalizationTypography
                variant="Inter/Body-sm"
                color="#6B7280"
                textId="app.rentalPage.stepOne.noCountryRegionFound"
                defaultText="No country found."
              />
            )}
          </RadioGroup>
        </Box>
      </DialogContent>
    </FullHeightSlideDialog>
  );
};
